/* eslint-disable max-lines */
import { GetConfigValueResponsePayload } from "@swiggy-private/native-bridge";
import Analytics, {
    DEFAULT_ANALYTICS_CONTEXT_VALUE,
    AnalyticsContextValue,
    getAnalyticsConfigParams,
} from "@swiggy-private/analytics";
import type { ISwiggyHeaders } from "@swiggy-private/swiggy-money-context/src/interfaces";
import type { RefundPreferenceWidgetState } from "@swiggy-private/swiggy-money-ui/src/RefundPreferenceWidget/constants";

const ANALYTICS_HOST = "https://analytics.swiggy.com";
export const SCREEN_NAME = {
    LEDGER: "swiggy-money-page",
    HALF_CARD: "swiggy-money-halfcard",
    VOUCHER: "swiggy-money-voucher",
    TOPUP: "swiggy-money-topup-page",
    SEND_GIFT_VOUCHER: "send-gift-voucher",
    SEND_GIFT_VOUCHER_SUCCESS: "send-gift-voucher-success",
    ADD_GIFT_VOUCHER: "add-gift-voucher",
};

export const getAnalyticsEvents = (headers: ISwiggyHeaders, dynamicFlags: GetConfigValueResponsePayload) => {
    const analyticsConfig = getAnalyticsConfigParams(
        headers ?? {},
        process.env.NEXT_PUBLIC_ANALYTICS_HOST || ANALYTICS_HOST,
    );
    const analyticsEvents =
        (analyticsConfig &&
            new Analytics({
                ...analyticsConfig,
                shouldGZipEvents: dynamicFlags.wpValues?.web_gzip_analytics_message === "true",
            })) ||
        DEFAULT_ANALYTICS_CONTEXT_VALUE;
    return analyticsEvents;
};

// Voucher Events
const impressionVoucherPage = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    availBalance: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.VOUCHER,
        action: "impression-swiggy-money-voucher",
        context: JSON.stringify({ availBalance }),
    });
};

const impressionSmVoucherError = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    errorMessage: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.VOUCHER,
        action: "impression-voucher-error",
        context: JSON.stringify({ errorMessage }),
    });
};

const impressionSmVoucherErrorHalfCard = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    errorMessage: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.HALF_CARD,
        action: "impression-sm-voucher-error",
        context: JSON.stringify({ errorMessage }),
    });
};

const onRedeemVoucherClick = (clickEvent: AnalyticsContextValue["clickEvent"], amount?: string | number): void => {
    clickEvent({
        category: SCREEN_NAME.VOUCHER,
        action: "click-redeem-voucher",
        context: JSON.stringify({ amount }),
    });
};

const onAddGiftCardTopupClick = (clickEvent: AnalyticsContextValue["clickEvent"], amount?: string | number): void => {
    clickEvent({
        category: SCREEN_NAME.TOPUP,
        action: "click-add-gift-card",
        context: JSON.stringify({ amount }),
    });
};

const onErrorOkayClick = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.HALF_CARD,
        action: "click-okay",
    });
};

export const getVoucherEvents = (analyticsEvents: AnalyticsContextValue) => {
    return {
        ...analyticsEvents,
        onRedeemVoucherClick,
        onAddGiftCardTopupClick,
        impressionVoucherPage,
        impressionSmVoucherError,
        impressionSmVoucherErrorHalfCard,
        onErrorOkayClick,
    };
};

// Ledger Events
const impressionLedgerPage = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    availBalance: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.LEDGER,
        action: "impression-swiggy-money",
        context: JSON.stringify({ availBalance }),
    });
};

const onLedgerRedeemNowClick = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-sm-redeem-now",
    });
};

const onLedgerAddGiftCardClick = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-add-gift-card",
    });
};

const onLedgerFaqClick = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-sm-faq",
    });
};

const onLedgerHowItWorksClick = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-how-sm-works",
    });
};

const onLedgerLatestUpdatesClick = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-latest-updates",
    });
};

const onLoadOlderTransactionsClick = (clickEvent: AnalyticsContextValue["clickEvent"], txnTab: string): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-show-older-transactions",
        label: txnTab,
    });
};

const clickBuyGiftVoucher = (clickEvent: AnalyticsContextValue["clickEvent"]) => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-buy-gift-voucher",
    });
};

const impressionBannerInstantRefund = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    bannerText: string,
    refundSelectionStatus: RefundPreferenceWidgetState,
): void => {
    impressionEvent({
        category: SCREEN_NAME.LEDGER,
        action: "impression-banner-get-instant-refund",
        label: bannerText,
        context: JSON.stringify({ refundSelectionStatus }),
    });
};

const impressionBannerButtonInstantRefund = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    buttonText: string,
    refundSelectionStatus: RefundPreferenceWidgetState,
): void => {
    impressionEvent({
        category: SCREEN_NAME.LEDGER,
        action: "impression-banner-button-get-instant-refund",
        label: buttonText,
        context: JSON.stringify({ refundSelectionStatus }),
    });
};

const clickBannerRefundMethod = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    buttonText: string,
    refundSelectionStatus: RefundPreferenceWidgetState,
): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-banner-refund-method",
        label: buttonText,
        context: JSON.stringify({ refundSelectionStatus }),
    });
};

const impressionRefundOptionsBottomSheet = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    refundSelectionStatus: RefundPreferenceWidgetState,
): void => {
    impressionEvent({
        category: SCREEN_NAME.LEDGER,
        action: "impression-refund-option-half-card",
        context: JSON.stringify({ refundSelectionStatus }),
    });
};

const clickRefundOptionHalfCard = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    selectedOption: RefundPreferenceWidgetState,
): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-refund-option-half-card",
        context: selectedOption,
    });
};

const clickSaveRefundPreference = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    refundSelectionStatus: RefundPreferenceWidgetState,
): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "save-preference-refund-option",
        context: refundSelectionStatus,
    });
};

const clickCrossRefundOptionsBottomSheet = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-cross-refund-option",
    });
};

const impressionRefundOptionUpdated = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    refundSelectionStatus: RefundPreferenceWidgetState,
): void => {
    impressionEvent({
        category: SCREEN_NAME.LEDGER,
        action: "refund-method-updated",
        context: JSON.stringify({ refundSelectionStatus }),
    });
};

const impressionRefundOptionErrorCard = (impressionEvent: AnalyticsContextValue["impressionEvent"]): void => {
    impressionEvent({
        category: SCREEN_NAME.LEDGER,
        action: "save-preference-error-screen",
    });
};

const clickRetryOnRefundOptionErrorCard = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.LEDGER,
        action: "click-retry-refund-preference",
    });
};

export const getLedgerEvents = (analyticsEvents: AnalyticsContextValue) => {
    return {
        ...analyticsEvents,
        onLedgerRedeemNowClick,
        onLedgerAddGiftCardClick,
        onLedgerHowItWorksClick,
        onLedgerLatestUpdatesClick,
        onLedgerFaqClick,
        onLoadOlderTransactionsClick,
        impressionLedgerPage,
        clickBuyGiftVoucher,
        impressionBannerInstantRefund,
        impressionBannerButtonInstantRefund,
        clickBannerRefundMethod,
        impressionRefundOptionsBottomSheet,
        clickRefundOptionHalfCard,
        clickSaveRefundPreference,
        clickCrossRefundOptionsBottomSheet,
        impressionRefundOptionUpdated,
        impressionRefundOptionErrorCard,
        clickRetryOnRefundOptionErrorCard,
    };
};
